import lodash from 'lodash';
import moment from 'moment';
import Modal from 'react-modal';
import classNames from 'classnames';
import React, { useState, useMemo } from 'react';
import { LeapfrogHistory } from 'interface/createUpdateForm';

import DropDown from 'components/common/dropDown';

// Components
import Loading from 'components/common/loading/Loading';
import InputWrapper from 'components/common/inputWrapper';
import DatePicker from 'components/common/datepicker/DatePicker';
// eslint-disable-next-line max-len
import TerminationRestrictionInfo from 'pages/employees/profile/changeEngagement/components/TerminationRestrictionInfo';
// eslint-disable-next-line max-len
import TerminationConfirmationModal from 'pages/employees/profile/changeEngagement/components/TerminationConfirmationModal';

import { getRequiredErrorLabel } from 'utils/string';
import { combineArrayObjects, isObjectEmpty } from 'utils/object';
import {
  sortByDate,
  getFormattedDate,
  getMonthsLaterDate,
  isSubContractEndDateOutsideRange,
  isSubContractStartDateOutsideRange,
} from 'utils/date';

import { TERMINATION_CONFIRMATION_TITLE } from 'constants/appConstants';
import { PROBATION, FIXED_TERM_CONTRACT, HIRED, TERMINATED, ENGAGEMENT_STATUS_IDS } from 'constants/employeeStatus';

import EmployeeStatusDetails from './EmployeeStatusDetails';
import { DropdownOption } from 'components/common/dropDown/Dropdown';
import { GenericRecord } from 'interface/common';

Modal.setAppElement('#root');

interface EngagementStatusOption {
  id: number;
  name: string;
}

type EmpStatusHistoryItem = LeapfrogHistory['empStatusHistory'][number] & {
  endDate?: string | null;
  empStatusDetails?: Array<{ id?: number; category: string; date?: string }>;
};

interface EngagementModalValues {
  joinDate: string | Date;
  empStatusHistory: EmpStatusHistoryItem[];
  firstName?: string;
  lastName?: string;
}

interface EngagementModalTouched {
  empStatusHistory?: {
    engagementStatus?: boolean;
    transitionDate?: boolean;
    endDate?: boolean;
    empStatusDetails?: Array<{ category?: boolean; date?: boolean }>;
  };
}

interface AddEngagementStatusModalProps {
  values: EngagementModalValues;
  touched: EngagementModalTouched;
  handleChange: (event: { target: { name: string; value: EmpStatusHistoryItem[] } }) => void;
  handleBlur: (event: { target: { name: string } }) => void;
  isOpen: boolean;
  onClose: (editIndex?: number) => void;
  buttonLabel: string;
  setFieldTouched: (name: string, touched: boolean) => void;
  engagementStatus: EngagementStatusOption[];
  editIndex?: number;
  isCreateForm: boolean;
  initialValues: { firstName?: string; lastName?: string };
  empId?: string | number | null;
}

interface EngagementModalErrors {
  engagementStatus?: string | false;
  transitionDate?: string | false;
  endDate?: string | false;
  empStatusDetails?: Array<{ category?: string | false; date?: string | false }>;
}

interface EngagementStatusPayload extends EmpStatusHistoryItem {
  transitionDate: string | Date;
  engagementStatus: EngagementStatusOption;
}

const buildInitialPayload = (joinDate: string | Date, isCreateForm: boolean): Partial<EngagementStatusPayload> => ({
  transitionDate: isCreateForm ? joinDate : getFormattedDate(),
});

const normalizeEmployeeStatusPayload = (
  payload: Partial<EngagementStatusPayload>
): Partial<EngagementStatusPayload> => {
  const nextPayload = { ...payload };

  if (
    nextPayload.engagementStatus?.name !== FIXED_TERM_CONTRACT &&
    nextPayload.engagementStatus?.id !== ENGAGEMENT_STATUS_IDS.SUB_CONTRACT
  ) {
    delete nextPayload.endDate;
  }

  if (![PROBATION, FIXED_TERM_CONTRACT].includes(nextPayload.engagementStatus?.name)) {
    delete nextPayload.empStatusDetails;
  }

  if (
    nextPayload.engagementStatus?.name === FIXED_TERM_CONTRACT &&
    nextPayload.transitionDate &&
    !nextPayload.endDate
  ) {
    nextPayload.endDate = getMonthsLaterDate(nextPayload.transitionDate, 6);
  }

  return nextPayload;
};

const AddEngagementStatusModalContent: React.FC<AddEngagementStatusModalProps> = (props) => {
  const {
    values,
    touched,
    handleChange,
    handleBlur,
    isOpen,
    onClose,
    buttonLabel,
    setFieldTouched,
    engagementStatus,
    editIndex,
    isCreateForm,
    initialValues,
    empId,
  } = props;

  const { joinDate } = values;

  const [isLoading, setIsLoading] = useState(false);
  const initialPayload = useMemo(
    () => buildInitialPayload(values.joinDate, isCreateForm),
    [isCreateForm, values.joinDate]
  );
  const basePayload = useMemo(
    () =>
      normalizeEmployeeStatusPayload(
        isNaN(editIndex) ? initialPayload : values.empStatusHistory[editIndex] || initialPayload
      ),
    [editIndex, initialPayload, values.empStatusHistory]
  );
  const [employeeStatusPayload, setEmployeeStatusPayload] = useState<Partial<EngagementStatusPayload>>(basePayload);

  const getSelectedOption = (item: DropdownOption | readonly DropdownOption[] | null): DropdownOption | null => {
    if (!item || Array.isArray(item) || !('value' in item)) {
      return null;
    }

    return { label: item.label, value: item.value };
  };
  const [openTerminationConfirmationModal, setOpenTerminationConfirmationModal] = useState(false);
  const [isTerminatedSelected, setIsTerminatedSelected] = useState(false);
  const [isNotAllowedToTerminate, setIsNotAllowedToTerminate] = useState(false);

  const updateEmployeeStatusPayload: React.Dispatch<React.SetStateAction<Partial<EngagementStatusPayload>>> = (
    value
  ) => {
    setEmployeeStatusPayload((previousPayload) =>
      normalizeEmployeeStatusPayload(typeof value === 'function' ? value(previousPayload) : value)
    );
  };

  const engagementStatusOptions = useMemo(
    () => engagementStatus.map((status) => ({ label: status?.name, value: status.id })),
    [engagementStatus]
  );

  const getDefaultValue = () => {
    if (isObjectEmpty(employeeStatusPayload.engagementStatus)) {
      return null;
    }

    if (!employeeStatusPayload.engagementStatus) {
      return null;
    }

    return {
      label: employeeStatusPayload.engagementStatus?.name,
      value: employeeStatusPayload.engagementStatus?.id,
    };
  };

  const error = useMemo<EngagementModalErrors>(() => {
    if (!touched.empStatusHistory) {
      return {};
    }

    const { engagementStatus, empStatusDetails, transitionDate, endDate } = employeeStatusPayload;

    const errorArray: EngagementModalErrors[] = [];

    if (touched.empStatusHistory.engagementStatus && !engagementStatus) {
      errorArray.push({ engagementStatus: getRequiredErrorLabel('employment status') });
    }

    if (touched.empStatusHistory.transitionDate && !transitionDate) {
      errorArray.push({ transitionDate: getRequiredErrorLabel('start date') });
    }

    if (
      touched.empStatusHistory.transitionDate &&
      engagementStatus?.name === HIRED &&
      moment(transitionDate).isAfter(joinDate)
    ) {
      errorArray.push({ transitionDate: 'Start date cannot be before join date' });
    }

    if (engagementStatus?.name === FIXED_TERM_CONTRACT) {
      errorArray.push({ endDate: touched.empStatusHistory.endDate && !endDate && getRequiredErrorLabel('end date') });
    }

    if ([PROBATION, FIXED_TERM_CONTRACT].includes(engagementStatus?.name)) {
      errorArray.push({
        empStatusDetails: empStatusDetails?.map((empStatusDetailsItem, index) => {
          return {
            category:
              Boolean(
                touched.empStatusHistory?.empStatusDetails &&
                touched.empStatusHistory?.empStatusDetails[index]?.category &&
                !empStatusDetailsItem?.category
              ) && getRequiredErrorLabel('category'),
            date:
              Boolean(
                touched.empStatusHistory?.empStatusDetails &&
                touched.empStatusHistory?.empStatusDetails[index]?.date &&
                !empStatusDetailsItem?.date
              ) && getRequiredErrorLabel('date'),
          };
        }),
      });
    }

    const finalError = combineArrayObjects(errorArray as GenericRecord[]);

    return {
      ...finalError,
    };
  }, [touched, employeeStatusPayload, joinDate]);

  const showTerminationConfirmationModal = () => {
    setOpenTerminationConfirmationModal(true);
  };

  const closeTerminationConfirmationModal = () => {
    setOpenTerminationConfirmationModal(false);

    onClose();
  };

  const handleCancel = () => {
    setEmployeeStatusPayload(initialPayload);
    setFieldTouched('empStatusHistory', false);
    setIsTerminatedSelected(false);
    onClose();
  };

  const handleEngagementStatusSave = () => {
    const { engagementStatus, transitionDate, empStatusDetails, endDate } = employeeStatusPayload;

    const hasValidCategories =
      [PROBATION, FIXED_TERM_CONTRACT].includes(engagementStatus?.name) &&
      !empStatusDetails?.every(({ category }) => category);

    const hasValidCategoryDates =
      [PROBATION, FIXED_TERM_CONTRACT].includes(engagementStatus?.name) && !empStatusDetails?.every(({ date }) => date);

    const isValidEnagementStatusDate =
      (engagementStatus?.name === FIXED_TERM_CONTRACT && !endDate) ||
      (engagementStatus?.name === HIRED && moment(transitionDate).isAfter(joinDate));

    if (
      !engagementStatus ||
      !transitionDate ||
      hasValidCategories ||
      hasValidCategoryDates ||
      isValidEnagementStatusDate
    ) {
      handleBlur({
        target: {
          name: 'empStatusHistory.engagementStatus',
        },
      });
      handleBlur({
        target: {
          name: 'empStatusHistory.transitionDate',
        },
      });
      handleBlur({
        target: {
          name: 'empStatusHistory.endDate',
        },
      });

      if (empStatusDetails) {
        empStatusDetails.forEach((empStatusDetail, index) => {
          handleBlur({ target: { name: `empStatusHistory.empStatusDetails[${index}].category` } });
          handleBlur({ target: { name: `empStatusHistory.empStatusDetails[${index}].date` } });
        });
      }

      return;
    }

    let value;

    if (!isNaN(editIndex)) {
      value = [...values.empStatusHistory];
      value[editIndex] = employeeStatusPayload;
    } else {
      value = [employeeStatusPayload, ...values.empStatusHistory];
    }

    sortByDate(value, 'transitionDate');

    setIsLoading(true);
    handleChange({
      target: {
        name: 'empStatusHistory',
        value,
      },
    });
    setEmployeeStatusPayload(initialPayload);
    setFieldTouched('empStatusHistory', false);
    setIsLoading(false);

    if (engagementStatus?.name === TERMINATED) {
      closeTerminationConfirmationModal();
    }

    onClose();
  };

  const onEngagementStatusConfirm = () => {
    const { engagementStatus } = employeeStatusPayload;

    if (engagementStatus.name === TERMINATED) {
      showTerminationConfirmationModal();

      onClose(editIndex);

      return;
    }

    setIsTerminatedSelected(false);

    handleEngagementStatusSave();
  };

  const getMostRecentTransitionDate = () => {
    const descendingEmpStatusHistory = lodash.orderBy(values.empStatusHistory, 'transitionDate', 'desc');

    let currentDesignationIndex = 0;
    descendingEmpStatusHistory.forEach((data) => {
      if (getFormattedDate() < data.transitionDate) {
        currentDesignationIndex++;
      }
    });

    return descendingEmpStatusHistory.length > 0 && currentDesignationIndex < descendingEmpStatusHistory.length
      ? descendingEmpStatusHistory[currentDesignationIndex].transitionDate
      : values.joinDate;
  };

  const isValidStartDate = (date) => {
    if (employeeStatusPayload.engagementStatus?.name === HIRED) {
      return !date.isSameOrBefore(values.joinDate, 'days');
    }

    return date.isBefore(values.joinDate);
  };

  const isValidSubContractStartDate = (date) => {
    const mostRecentTransitionDate = getMostRecentTransitionDate();

    return isSubContractStartDateOutsideRange(
      date,
      employeeStatusPayload.endDate ? moment(employeeStatusPayload.endDate) : null,
      moment(mostRecentTransitionDate)
    );
  };

  const handleSubContractEndDateChange = (date) => {
    updateEmployeeStatusPayload((payload) => ({ ...payload, endDate: getFormattedDate(date) }));
  };

  const isValidEndDate = (date) => {
    const mostRecentTransitionDate = getMostRecentTransitionDate();

    return isSubContractEndDateOutsideRange(
      date,
      employeeStatusPayload.transitionDate ? moment(employeeStatusPayload.transitionDate) : null,
      moment(mostRecentTransitionDate)
    );
  };

  const buttonText = employeeStatusPayload?.engagementStatus?.name !== TERMINATED ? `${buttonLabel} & Close` : 'Next';

  return (
    <>
      <TerminationConfirmationModal
        isOpen={openTerminationConfirmationModal}
        title={{
          text: TERMINATION_CONFIRMATION_TITLE,
          type: `danger`,
        }}
        firstName={initialValues.firstName}
        lastName={initialValues.lastName}
        onConfirm={handleEngagementStatusSave}
        onCancel={closeTerminationConfirmationModal}
      />

      <Modal
        isOpen={isOpen}
        className="lf-modal lf-modal--engagement-status"
        contentLabel="Example Modal"
        overlayClassName="lf-modal__overlay"
        bodyOpenClassName="overflow-hidden"
      >
        <div className="add-designation-modal">
          <p className="add-designation-modal__header">{buttonLabel === 'Add' ? 'Add' : 'Edit'} Employment Status</p>

          <div className="add-designation-modal__form">
            <div className="add-designation-modal__form__full-width">
              <div className="add-designation-modal__form__full-width__space">
                <InputWrapper label="Employment Status" error={error.engagementStatus} isMandatory>
                  <DropDown
                    hasError={Boolean(error.engagementStatus)}
                    value={getDefaultValue()}
                    onDropDownChange={(item) => {
                      const selectedItem = getSelectedOption(item);
                      if (!selectedItem) {
                        return;
                      }
                      const selectedEngagementStatus = engagementStatus.find(
                        (status) => status.id === selectedItem.value
                      );
                      if (!selectedEngagementStatus) {
                        return;
                      }
                      updateEmployeeStatusPayload((payload) => ({
                        ...payload,
                        engagementStatus: selectedEngagementStatus,
                      }));

                      setIsTerminatedSelected(selectedEngagementStatus.id === ENGAGEMENT_STATUS_IDS.TERMINATED);
                    }}
                    onBlur={() => handleBlur({ target: { name: 'empStatusHistory.engagementStatus' } })}
                    options={engagementStatusOptions}
                    isSearchable={true}
                    isDisabled={false}
                    customClassName="employee-form__dropdown"
                  />
                </InputWrapper>
              </div>
              <div className="add-designation-modal__form__full-width__space">
                <InputWrapper
                  label="Start Date"
                  error={error.transitionDate}
                  isMandatory
                  className="employee-form__date"
                >
                  <DatePicker
                    hasError={Boolean(error.transitionDate)}
                    date={employeeStatusPayload.transitionDate ? moment(employeeStatusPayload.transitionDate) : null}
                    onDateChange={(date) =>
                      updateEmployeeStatusPayload((payload) => ({
                        ...payload,
                        transitionDate: getFormattedDate(date),
                        ...(payload.engagementStatus?.name === FIXED_TERM_CONTRACT && {
                          endDate: moment(getMonthsLaterDate(date, 6)),
                        }),
                        ...(payload.engagementStatus?.id === ENGAGEMENT_STATUS_IDS.SUB_CONTRACT &&
                          payload.endDate &&
                          moment(payload.endDate).isSameOrBefore(date) && {
                            endDate: null,
                          }),
                      }))
                    }
                    onClose={() => handleBlur({ target: { name: 'empStatusHistory.transitionDate' } })}
                    placeholderText="Pick a Start Date"
                    showMonthDropdown={true}
                    showYearDropdown={true}
                    isOutsideRange={
                      employeeStatusPayload.engagementStatus?.id === ENGAGEMENT_STATUS_IDS.SUB_CONTRACT
                        ? isValidSubContractStartDate
                        : isValidStartDate
                    }
                  />
                </InputWrapper>
              </div>

              {employeeStatusPayload?.engagementStatus?.name === FIXED_TERM_CONTRACT && (
                <div className="add-designation-modal__form__full-width__space">
                  <InputWrapper label="End Date" error={error.endDate} isMandatory className="employee-form__date">
                    <DatePicker
                      hasError={Boolean(error.endDate)}
                      date={employeeStatusPayload.endDate ? moment(employeeStatusPayload.endDate) : null}
                      onDateChange={(date) =>
                        updateEmployeeStatusPayload((payload) => ({ ...payload, endDate: getFormattedDate(date) }))
                      }
                      isOutsideRange={(date) => date.isBefore(employeeStatusPayload.transitionDate)}
                      onClose={() => handleBlur({ target: { name: 'empStatusHistory.endDate' } })}
                    />
                  </InputWrapper>
                </div>
              )}
              {employeeStatusPayload?.engagementStatus?.id === ENGAGEMENT_STATUS_IDS.SUB_CONTRACT && (
                <div className="add-designation-modal__form__full-width__space">
                  <InputWrapper
                    label="End Date"
                    error={error.endDate}
                    isMandatory={false}
                    className="employee-form__date"
                  >
                    <DatePicker
                      hasError={Boolean(error.endDate)}
                      date={employeeStatusPayload.endDate ? moment(employeeStatusPayload.endDate) : null}
                      onDateChange={handleSubContractEndDateChange}
                      isOutsideRange={isValidEndDate}
                      onClose={() => handleBlur({ target: { name: 'empStatusHistory.endDate' } })}
                      placeholderText="Pick an End Date"
                      showMonthDropdown={true}
                      showYearDropdown={true}
                    />
                  </InputWrapper>
                </div>
              )}
            </div>

            {isTerminatedSelected && (
              <TerminationRestrictionInfo
                userFirstName={initialValues.firstName}
                userId={empId}
                setIsNotAllowedToTerminate={setIsNotAllowedToTerminate}
                isScrollable
              />
            )}

            {[PROBATION, FIXED_TERM_CONTRACT].includes(employeeStatusPayload?.engagementStatus?.name) && (
              <EmployeeStatusDetails
                employeeStatusPayload={employeeStatusPayload as import('./EmployeeStatusDetails').EmployeeStatusPayload}
                setEmployeeStatusPayload={updateEmployeeStatusPayload}
                handleBlur={handleBlur}
                error={error}
                editIndex={editIndex}
              />
            )}
          </div>
          <div className="add-designation-modal__bottom">
            <button
              className={classNames('btn', {
                'btn--primary': !isNotAllowedToTerminate || !isTerminatedSelected,
                'btn--loading': isLoading,
                'btn--disabled': isNotAllowedToTerminate && isTerminatedSelected,
              })}
              disabled={isLoading || (isNotAllowedToTerminate && isTerminatedSelected)}
              onClick={onEngagementStatusConfirm}
            >
              <span className="btn__label">{buttonText}</span>
              {isLoading && <Loading />}
            </button>
            <button className="btn btn--outlined-grey ml-md-24" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
const AddEngagementStatusModal: React.FC<AddEngagementStatusModalProps> = (props) => {
  const modalKey =
    `${props.editIndex ?? 'new'}-${String(props.values.joinDate)}` +
    `-${props.values.empStatusHistory.length}-${props.isOpen ? 'open' : 'closed'}`;

  return <AddEngagementStatusModalContent key={modalKey} {...props} />;
};

export default AddEngagementStatusModal;
